<template>
  <div class="py-4">
    <div class="flex flex-col lg:flex-row">
      <div class="flex-shrink-0 flex items-start lg:w-144 lg:pr-12">
        <div
          class="relative flex-shrink-0 w-40 mr-6 lg:mr-12"
          :class="{ 'hidden lg:block': !image }"
        >
          <img :src="pictureIcon"
               :alt="name"
               class="absolute h-auto z-1"
               :style="{
                 width: `${pictureIconWidthPercentage}%`,
                 top: iconImagePosition.top,
                 left: iconImagePosition.left,
               }"
               ref="iconImageEl"
               v-if="pictureIcon"
          />

          <div class="absolute top-0 left-0 h-auto text-[color:var(--color)] bg-[color:var(--bg-color)] z-1 px-2 rounded-10px text-90% break-all leading-none py-1.5" :style="{
            '--color': pictureTextColor,
            '--bg-color': pictureTextBackgroundColor,
          }" v-else-if="pictureText">
            {{ pictureText}}
          </div>

          <a
            :href="
              resizeImage(image, {
                w: 1400,
                fit: 'max',
                fm: 'jpg',
              })
            "
            class="dynamic-zoom block"
            v-if="image"
          >
            <img
              :src="
                resizeImage(image, {
                  w: 100,
                })
              "
              :alt="name"
              loading="lazy"
              width="100"
              height="100"
              class="transition-opacity duration-300 opacity-0"
              @load="imageLoaded($event.target)"
            />
          </a>
        </div>

        <div class="flex flex-col h-full">
          <div class="text-8 text-blue-900 font-bold lg:text-7">
            {{ __('room_title', { number: number }) }}
          </div>
          <h3
            class="text-7 text-blue-900 font-normal leading-tight mb-2 lg:text-6.5"
          >
            {{ name }}
          </h3>

          <div
            class="text-6 text-blue-900 font-normal leading-tight"
            v-if="dayPrices"
          >
            {{ dayPrices }}
          </div>

          <div class="pt-1 mt-auto" v-if="description">
            <InfoButton @click="showDescription = !showDescription"
              >{{ __('btn_info') }}
            </InfoButton>
          </div>
        </div>
      </div>

      <div class="mt-6 mb-6" v-if="showDescription && !isLargeScreen">
        <article v-html="description"></article>
      </div>

      <div class="flex-grow overflow-x-auto mt-4 lg:mt-0">
        <div
          class="grid flex-grow grid-cols-7 gap-6 min-w-472 lg:min-w-0"
          v-if="!isLargeScreen"
        >
          <div
            class="text-12 text-blue-900 font-bold"
            v-for="day in currentWeekDays"
            :key="day.date"
          >
            {{
              day.shortName
            }}
            <span class="text-8 font-normal ml-2">{{ day.date }}</span>
          </div>
        </div>

        <div
          class="flex-grow grid grid-cols-7 gap-6 min-w-472 lg:min-w-0 xl:gap-12"
        >
          <div
            class="space-y-3"
            v-for="(timetable, timetableIndex) in computedTimetable"
            :key="timetableIndex"
          >
            <DayButton
              v-for="timetableItem in timetable"
              :key="timetableItem.code"
              :disabled="timetableItem.disabled"
              :selected="
                selectedTimeCode &&
                selectedTimeCode === timetableItem.code &&
                !timetableItem.disabled
              "
              @click="updateSelectedRoomData(timetableItem.code)"
            >
              {{ timetableItem.start }} - {{ timetableItem.end }}
            </DayButton>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-6" v-if="showDescription && isLargeScreen">
      <article v-html="description"></article>
    </div>
  </div>
</template>

<script>
import __ from '@/libraries/Translation'
import {useElementSize, useMediaQuery} from '@vueuse/core'
import { formatPrice, resizeImage } from '@/libraries/Helpers'
import { computed, ref } from 'vue'
import { currentWeekDays } from '@/stores/Weekdays'
import {
  selectedTimeCode,
  setSelectedRoomId,
  setSelectedTimeCode,
} from '@/stores/Rooms'

import DayButton from '@/components/Room/DayButton'
import InfoButton from '@/components/Room/InfoButton'

export default {
  components: {
    DayButton,
    InfoButton,
  },
  props: {
    id: {
      type: Number,
      required: true,
    },
    roomHash: {
      type: String,
      required: true,
    },
    name: {
      type: String,
      default: '',
      required: true,
    },
    description: {
      type: String,
      default: '',
      required: true,
    },
    number: {
      type: [String, Number],
      default: '',
      required: true,
    },
    image: {
      type: String,
      default: '',
      required: true,
    },
    prices: {
      type: Object,
      required: true,
    },
    timetable: {
      type: Object,
      required: true,
    },
    currency: {
      type: String,
      required: true,
    },
    pictureIcon: {
      type: String,
      default: '',
    },
    pictureIconWidthPercentage: {
      type: Number,
      default: 25,
    },
    pictureText: {
      type: String,
      default: '',
    },
    pictureTextColor: {
      type: String,
      default: '',
    },
    pictureTextBackgroundColor: {
      type: String,
      default: '',
    },
    pictureIconXPercentage: {
      type: Number,
      default: 0,
    },
    pictureIconYPercentage: {
      type: Number,
      default: 0,
    },
  },
  emits: ['change'],
  setup(props, { emit }) {
    const showDescription = ref(false)

    const computedTimetable = computed(() => {
      const timetable = {}
      for (let i = 1; i <= 7; i++) {
        timetable[i] = props.timetable[i] || []
      }
      return timetable
    })

    const updateSelectedRoomData = (code) => {
      setSelectedRoomId(props.id)
      setSelectedTimeCode(code)
      emit('change', code)
    }

    const imageLoaded = (target) => {
      target.classList.remove('opacity-0')
    }

    const dayPrices = computed(() => {
      const daysByPrices = {}
      for (const [day, price] of Object.entries(props.prices)) {
        if (!daysByPrices[price]) {
          daysByPrices[price] = []
        }
        daysByPrices[price].push(day.toString())
      }

      const collect = []

      for (const [price, days] of Object.entries(daysByPrices)) {
        const collectDays = []
        let counter = 0
        for (let i = 1; i <= 7; i++) {
          if (days.includes(i.toString())) {
            if (!collectDays[counter]) {
              collectDays[counter] = {
                price: parseFloat(price),
                days: [],
              }
            }
            collectDays[counter].days.push(i)
          } else {
            counter++
          }
        }

        collectDays.forEach((daysBlock) => {
          daysBlock.first = Math.min(...daysBlock.days)
          daysBlock.last = Math.max(...daysBlock.days)
          collect.push(daysBlock)
        })
      }

      collect.sort((a, b) => a.first - b.last)

      const parts = []
      collect.forEach((collectedItem) => {
        if (collectedItem.first !== collectedItem.last) {
          parts.push(
            `${__(`day_name_short_${collectedItem.first}`)}-${__(
              `day_name_short_${collectedItem.last}`
            )} ${props.currency}${formatPrice(collectedItem.price)}`
          )
        } else {
          parts.push(
            `${__(`day_name_short_${collectedItem.first}`)} ${
              props.currency
            }${formatPrice(collectedItem.price)}`
          )
        }
      })

      return parts.join(' / ')
    })

    const isLargeScreen = useMediaQuery('(min-width: 1024px)')

    const iconImageEl = ref(null)
    const { width: iconImageWidth, height: iconImageHeight } = useElementSize(iconImageEl)

    const iconImagePosition = computed(() => {
      return {
        top: `calc(${props.pictureIconYPercentage}% - ${iconImageHeight.value / 2}px)`,
        left: `calc(${props.pictureIconXPercentage}% - ${iconImageWidth.value / 2}px)`,
      }
    })

    return {
      showDescription,
      computedTimetable,
      selectedTimeCode,
      updateSelectedRoomData,
      formatPrice,
      resizeImage,
      imageLoaded,
      dayPrices,
      isLargeScreen,
      currentWeekDays,
      iconImageEl,
      iconImagePosition
    }
  },
}
</script>
